import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { PlannerStore } from '../../../store/Planner';
import { UserStore } from '../../../store/User';
import './PlannerModal.scss';
import { observer } from 'mobx-react';
import {
  Button,
  FormItem,
  Modal,
  notification,
  NotificationType,
  TextInput
} from '@iba-team/portal-react-components/dist';
import { PlannerFilterDto, PlannerListDTO } from '../../../types';

import { useNavigate } from 'react-router';
import { PLANNER_ROUTE } from '../../../utils/consts';

export type NewListModalI = {
  onClose?: () => void,
  currentList: PlannerListDTO,
  addPlannerFilter: (value: PlannerFilterDto) => Promise<void>,
  getPlannerFilters: () => Promise<void>,
  doCreateEmptyList: boolean,
  onGoWithoutSaving? : () => void,
  onSubmit? : () => void,
  isForWhoisOffToday?: boolean
}

export const NewListModal = observer(({ addPlannerFilter, onClose, onGoWithoutSaving, onSubmit, currentList, getPlannerFilters, doCreateEmptyList, isForWhoisOffToday : initIsForWhoisOffToday = false} : NewListModalI) =>  {
  const { t } = useTranslation();
  const { createEmployeeList } = PlannerStore;
  const { profile } = UserStore;
  const [disabled, setDisabled] = useState(false);
  const [listName, setListName] = useState('');
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const isForWhoisOffTodayRef = useRef(initIsForWhoisOffToday);
  const navigate = useNavigate();

  useEffect(() => {
    textInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if(listName === '') {
      setDisabled(true);
    }
    if(listName !== '') {
      setDisabled(false);
    }
  }, [listName]);

  const submit = async () => {
    if (listName !== '') {
      await createEmployeeList({
        name: listName,
        ownerId: profile.id,
        userIds: doCreateEmptyList ? [] : currentList.userIds,
      }, isForWhoisOffTodayRef.current).then(res=> {

        // do cleanup to prevent multiple, redundant updates in 
        // widget_analytics
        isForWhoisOffTodayRef.current=false;

        addPlannerFilter([
          {
            element: 'SELECTED_LIST_NAME',
            value: res.name
          },
          {
            element: 'LIST_TYPE',
            value: res.plannerListType.type
          },
          {
            element: 'ID',
            value: res.id
          },

        ]).then(() => {

          // do cleanup to prevent multiple, redundant updates in 
          // widget_analytics
          isForWhoisOffTodayRef.current=false;

          getPlannerFilters();
        });
      });
      onClose?.();
      notification.open({
        type: NotificationType.SUCCESS,
        title: t('success'),
        description: t('createListSuccess')
      });
    }
  };

  const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit();
  };

  return(
    <Modal visible={true}
      className={'plannerModal'}
      footer={[
        <Button theme={{variant: 'neutral', color: 'grey'}} 
          onClick={() => { 
            // remove possible 'doShowNewListModal=true'
            navigate(PLANNER_ROUTE);
            onClose?.();
          }}> 
          { t('cancel') }
        </Button>,
        <Button theme={{variant: 'primary', color: 'green'}}
          onClick={() => {
            onSubmit?.();
            submit();
            // remove possible 'doShowNewListModal=true'
            navigate(PLANNER_ROUTE);
          }} disabled={disabled}>
          {t('newListModalSubmit')}
        </Button>
      ]}
      titleText={t('newListModalHeader')}
      onClose={() => onClose?.()}>
      <form onSubmit={onFormSubmit}>
        <FormItem isRequired  label={t('newListModalTitle')}>
          <TextInput
            maxLength={256}
            type={'text'}
            placeholder={t('newListModalPlaceholder')}
            ref={textInputRef}
            onChange={e => setListName(e.target.value)}
          />
        </FormItem>
      </form>
    </Modal>
  );
});
